import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';

import { selectSidebarExpanded } from 'src/app/stores/sidebar/sidebar.selectors';
import { hideSidebar, collapseSidebar, restoreSidebarVisibility } from 'src/app/stores/sidebar/sidebar.actions';
import { LayoutService } from 'src/app/stores/layout/layout.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

@Component({
    selector: 'app-main-wrapper',
    templateUrl: './main-wrapper.component.html',
    styleUrls: ['./main-wrapper.component.scss'],
    standalone: false
})
export class MainWrapperComponent implements OnInit, OnDestroy {
   private destroy$ = new Subject<void>();

   isSidebarOpen = false;
   isSidebarDisplayed = true;
   private isDesktop = false;
   private routeWantsAutoCollapse = false;

   private readonly DESKTOP_BREAKPOINT = '(min-width: 1200px)';

   constructor(
      private store: Store,
      private breakpointObserver: BreakpointObserver,
      private router: Router,
      private activatedRoute: ActivatedRoute,
      public layoutService: LayoutService
   ) { }

   ngOnInit(): void {
      // Check initial route state immediately
      let route = this.activatedRoute;
      while (route.firstChild) {
         route = route.firstChild;
      }
      this.routeWantsAutoCollapse = route.snapshot.data?.['autoCollapseSidebar'] === true;

      this.store
         .select(selectSidebarExpanded)
         .pipe(takeUntil(this.destroy$))
         .subscribe(isExpanded => {
            this.isSidebarOpen = isExpanded;
         });

      this.layoutService.sidebarVisible$
         .pipe(takeUntil(this.destroy$))
         .subscribe(isVisible => {
            this.isSidebarDisplayed = isVisible;
         });

      this.breakpointObserver
         .observe([this.DESKTOP_BREAKPOINT])
         .pipe(takeUntil(this.destroy$))
         .subscribe(state => {
           this.isDesktop = state.matches;
         });

      this.router.events
         .pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => {
               let route = this.activatedRoute;
               while (route.firstChild) {
                  route = route.firstChild;
               }
               return route.snapshot.data?.['autoCollapseSidebar'] === true;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe(shouldCollapse => {
            this.routeWantsAutoCollapse = shouldCollapse;

            // Desktop only
            if (!this.isDesktop) {
               return;
            }

            if (shouldCollapse) {
               // TEMPORARY route-based collapse
               this.store.dispatch(collapseSidebar());
            } else {
               // Restore persisted user preference
               this.store.dispatch(restoreSidebarVisibility());
            }
         });

      combineLatest([
         this.layoutService.sidebarVisible$,
         this.breakpointObserver.observe([this.DESKTOP_BREAKPOINT])
      ])
         .pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(
               ([prevVisible, prevBp], [currVisible, currBp]) =>
                  prevVisible === currVisible &&
                  prevBp.matches === currBp.matches
            )
         )
         .subscribe(([isSidebarVisible, breakpointState]) => {
            if (!isSidebarVisible || !breakpointState.matches) {
               this.store.dispatch(hideSidebar());
               return;
            }
          
            // Desktop + visible
            // Do NOT restore if route explicitly wants collapse
            if (!this.routeWantsAutoCollapse) {
               this.store.dispatch(restoreSidebarVisibility());
            } else {
               this.store.dispatch(collapseSidebar());
            }
         });
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }
}